/**
 * Hook for fetching YaaS yield distributions
 */

import { API_URLS, DEFAULT_QUERY_OPTIONS } from "@solomon-apps/shared";
import type { DistributionsResponse } from "@solomon-apps/shared";
import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";

import { demoDistributions } from "@/pages/Yaas/mocks/demoDistributions";

export interface UseDistributionsParams {
    address: string;
    page?: number;
    limit?: number;
}

export interface UseDistributionsResult {
    data: DistributionsResponse | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => void;
}

export async function fetchDistributions(
    address: string,
    page: number,
    limit: number,
    signal?: AbortSignal
): Promise<DistributionsResponse> {
    // Fetch boundary for the `?state=` demo frames. On a hit the request is
    // never issued, so the Transactions view is reviewable with no backend;
    // on a miss (every non-demo frame, and production builds, where the
    // fixture module is inert) this falls straight through to the real API.
    // See `pages/Yaas/mocks/demoDistributions.ts`.
    const demo = await demoDistributions(page, limit);
    if (demo !== null) return demo;

    const params = new URLSearchParams({
        limit: String(limit),
        page: String(page),
        sortOrder: "DESC"
    });

    const url = `${API_URLS.YAAS}/api/yaas/wallet/${address}/distributions?${params}`;
    const res = await fetch(url, { signal });

    if (!res.ok) {
        if (res.status === 404) {
            throw new Error("NOT_WHITELISTED");
        }
        throw new Error(`Failed to fetch distributions: ${res.status}`);
    }

    return res.json() as Promise<DistributionsResponse>;
}

export function useDistributions({ address, page = 1, limit = 10 }: UseDistributionsParams): UseDistributionsResult {
    const query = useQuery<DistributionsResponse, Error>({
        queryKey: ["yaas", "distributions", address, page, limit],
        queryFn: (context: QueryFunctionContext): Promise<DistributionsResponse> => {
            return fetchDistributions(address, page, limit, context.signal);
        },
        enabled: !!address,
        staleTime: 30 * 60 * 1000,
        refetchInterval: 30 * 60 * 1000,
        refetchOnWindowFocus: DEFAULT_QUERY_OPTIONS.refetchOnWindowFocus,
        refetchOnReconnect: DEFAULT_QUERY_OPTIONS.refetchOnReconnect,
        retry: (failureCount: number, error: Error): boolean => {
            if (error instanceof Error && error.message === "NOT_WHITELISTED") {
                return false;
            }
            return failureCount < 2;
        }
    });

    return {
        data: query.data ?? null,
        isLoading: query.isLoading,
        error: query.error ?? null,
        refetch: (): void => {
            void query.refetch();
        }
    };
}
